import { isFocusable } from './is-focusable.js';

/**
 * Checks whether the element or any of its ancestors is marked as `inert`.
 *
 * @internal
 */
export function isInInertSubtree(element: Element | null): boolean {
  let node: Element | null = element;
  while (node) {
    if (node.hasAttribute('inert')) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

function isRendered(element: HTMLElement): boolean {
  // `checkVisibility` covers `display: none`, the `hidden` attribute,
  // `visibility: hidden` and `content-visibility: hidden` in a single call.
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({
      visibilityProperty: true,
      contentVisibilityAuto: true,
    });
  }

  if (element.hidden) {
    return false;
  }

  let node: HTMLElement | null = element;
  while (node) {
    const { display, visibility, contentVisibility } = getComputedStyle(node);
    if (
      display === 'none' ||
      visibility === 'hidden' ||
      visibility === 'collapse' ||
      contentVisibility === 'hidden'
    ) {
      return false;
    }
    node = node.parentElement;
  }

  return true;
}

/**
 * Checks whether an element is reachable by pressing `Tab`. `isFocusable` only
 * inspects `tabIndex`, `nodeName` and `disabled`, so it also reports elements
 * the browser skips — those that are not rendered (`display: none`, `hidden`,
 * `visibility: hidden`, `content-visibility: hidden`) or that live inside an
 * `inert` subtree. Relying on `isFocusable` alone therefore yields tab-order
 * boundaries the browser never visits.
 *
 * @internal
 */
export function isTabbable(node: Node): boolean {
  if (!isFocusable(node)) {
    return false;
  }

  const element = node as HTMLElement;

  return isRendered(element) && !isInInertSubtree(element);
}
